import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Map, Calendar, Users, Sun, Sunset, Moon, MapPin, Clock, Lock, Plane } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedView() {
  const { linkId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [pin, setPin] = useState('');
  const [needsPin, setNeedsPin] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (pinVal) => {
    try {
      const res = await axios.get(`${API}/public/share/${linkId}`, { params: { pin: pinVal } });
      setData(res.data);
      setError('');
    } catch (e) {
      if (e.response?.status === 403) {
        setNeedsPin(true);
        setError('PIN requis');
      } else {
        setError(e.response?.data?.detail || 'Lien invalide ou expire');
      }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [linkId]); // eslint-disable-line

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EFF2F7] flex items-center justify-center">
        <div className="w-10 h-10 rounded-xl bg-[#3A86FF] flex items-center justify-center animate-pulse">
          <Plane className="w-5 h-5 text-white" />
        </div>
      </div>
    );
  }

  if (needsPin && !data) {
    return (
      <div className="min-h-screen bg-[#EFF2F7] flex items-center justify-center px-4">
        <div className="widget-card p-8 max-w-sm w-full text-center">
          <Lock className="w-10 h-10 text-[#3A86FF] mx-auto mb-4" />
          <h2 className="text-lg font-bold text-[#1E293B] mb-2">Acces protege</h2>
          <p className="text-sm text-[#64748B] mb-4">Entrez le code PIN pour acceder au voyage</p>
          <div className="flex gap-2">
            <Input value={pin} onChange={e => setPin(e.target.value)} placeholder="PIN" className="h-11 text-center text-lg" />
            <Button onClick={() => load(pin)} className="rounded-xl bg-[#3A86FF] text-white px-6">OK</Button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#EFF2F7] flex items-center justify-center">
        <div className="text-center"><p className="text-[#64748B]">{error || 'Lien invalide'}</p></div>
      </div>
    );
  }

  const { trip } = data;
  const blockIcons = { morning: Sun, afternoon: Sunset, evening: Moon };

  return (
    <div className="min-h-screen bg-[#EFF2F7]" data-testid="shared-view">
      {/* Header */}
      <div className="bg-white border-b border-[#E2E8F0] px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#3A86FF] flex items-center justify-center">
            <Plane className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#1E293B]">{trip.name}</h1>
            <p className="text-xs text-[#64748B]">{trip.destination} · {trip.start_date} - {trip.end_date}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Travelers */}
        {trip.travelers?.length > 0 && (
          <div className="widget-card p-4">
            <h3 className="text-sm font-bold text-[#1E293B] mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#3A86FF]" /> Voyageurs</h3>
            <div className="flex flex-wrap gap-2">
              {trip.travelers.map(t => (
                <Badge key={t.traveler_id} className="rounded-lg bg-[#F1F5F9] text-[#475569] border-0 text-xs px-3 py-1">{t.name} ({t.type})</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {trip.day_plans?.map(dp => {
          if (!dp.activities || dp.activities.length === 0) return null;
          return (
            <div key={dp.day_plan_id} className="widget-card p-4">
              <h3 className="text-sm font-bold text-[#1E293B] mb-3">
                <Calendar className="w-4 h-4 inline mr-1 text-[#3A86FF]" />
                {new Date(dp.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              <div className="space-y-2">
                {dp.activities.map(act => {
                  const BlockIcon = blockIcons[act.time_block] || Sun;
                  return (
                    <div key={act.activity_id} className="flex items-center gap-3 p-2 rounded-lg bg-[#F8FAFC]">
                      <BlockIcon className="w-4 h-4 text-[#F59E0B]" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1E293B]">{act.title}</p>
                        <div className="flex gap-2 text-[10px] text-[#94A3B8]">
                          {act.start_time && <span><Clock className="w-2.5 h-2.5 inline" /> {act.start_time}</span>}
                          {act.location && <span><MapPin className="w-2.5 h-2.5 inline" /> {act.location}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Journal */}
        {trip.journal?.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-[#1E293B] mb-3">Carnet de bord</h3>
            {trip.journal.map(entry => (
              <div key={entry.entry_id} className="widget-card p-4 mb-3">
                <p className="text-xs text-[#94A3B8] mb-1">{new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <p className="text-sm text-[#475569]">{entry.text}</p>
                {entry.ai_memory && <p className="text-xs text-[#3A86FF] italic mt-2">{entry.ai_memory}</p>}
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-[#94A3B8] py-4">Shared via LifePulse</p>
      </div>
    </div>
  );
}
